/**
 * EventsClient - Client for submitting CRM events to the backend.
 *
 * Handles event submission with proper error wrapping and correlation IDs.
 */

import { apiCall } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import type {
  EventBatchResponse,
  OverwriteTarget,
  PropertyValue,
} from '@/types/crm';

import {
  addPendingAssociation,
  addPendingCreation,
  applyAssociationChange,
  applyObjectDeleted,
  applyPropertyUpdate,
  removePendingAssociation,
} from '../store/crmObjectStore';
import {
  PropertyTypeConflictError,
  PropertyTypeError,
  PropertyTypePermissionError,
  PropertyTypeValidationError,
  translateEventError,
} from './errors';
import type { BaseEvent, EntityTypeName } from './types';

/**
 * Event batch structure for submission.
 */
export interface EventBatch {
  events: BaseEvent[];
  /**
   * Per-conflict authorizations to overwrite existing single-cardinality
   * associations. Each entry authorizes a removal scoped to one
   * (entity_id, association_type_name) pair. Omitted from the wire payload
   * when empty/undefined.
   */
  overwrite_targets?: OverwriteTarget[];
}

/**
 * Extract PropertyValue entries from raw event properties.
 */
function toPropertyValues(
  raw: Record<string, unknown>
): Record<string, PropertyValue> {
  const result: Record<string, PropertyValue> = {};
  for (const [key, value] of Object.entries(raw)) {
    result[key] = { value } as PropertyValue;
  }
  return result;
}

/**
 * Apply optimistic store updates from the backend response.
 *
 * After a successful submitEvents() call, this scans the **response results**
 * (not the submitted events) and updates the CRM store. Using response data
 * is critical because the backend may rewrite events — e.g., the unique
 * constraint validator can rewrite an object_created into a property_updated
 * on a different (hidden) entity. Using submitted events would create phantom
 * objects in the store.
 *
 * Handles:
 * - `ahoy_object_created`: Insert partial object + track as pending creation
 * - `ahoy_property_updated`: Merge updated properties into existing store entry
 * - `ahoy_object_deleted`: Mark object as deleted in the store
 * - `ahoy_association_added` (new entity in batch): Track pending association so
 *   hooks filtering by `associated_with_entity_id` can inject the new object
 * - `ahoy_association_added` (existing entity): Apply directly to the store so
 *   subscribers (e.g. the slideout via `subscribeToStoredObject`) update
 *   immediately, before the WebSocket broadcast arrives
 * - `ahoy_association_removed` (any entity): Apply directly to the store for the
 *   same immediate-update reason, and clear any pending-creation association
 *   tracking so filtered optimistic lists stop showing the entity under the
 *   removed association
 */
function processOptimisticUpdates(
  events: Array<{
    event_type_name: string;
    entity_id: string;
    entity_type_name: string;
    seq: number;
    payload: Record<string, unknown>;
  }>,
  orgId: number
): void {
  const createdIds = new Set<string>();

  for (const event of events) {
    switch (event.event_type_name) {
      case 'ahoy_object_created': {
        const rawProps = (event.payload?.properties ?? {}) as Record<
          string,
          unknown
        >;
        applyPropertyUpdate(
          event.entity_id,
          event.entity_type_name as EntityTypeName,
          toPropertyValues(rawProps),
          event.seq,
          orgId
        );
        addPendingCreation(event.entity_id);
        createdIds.add(event.entity_id);
        break;
      }

      case 'ahoy_property_updated': {
        const rawProps = (event.payload?.properties ?? {}) as Record<
          string,
          unknown
        >;
        if (Object.keys(rawProps).length > 0) {
          applyPropertyUpdate(
            event.entity_id,
            event.entity_type_name as EntityTypeName,
            toPropertyValues(rawProps),
            event.seq,
            orgId
          );
        }
        break;
      }

      case 'ahoy_object_deleted': {
        applyObjectDeleted(
          event.entity_id,
          event.seq,
          new Date().toISOString()
        );
        break;
      }

      case 'ahoy_association_added': {
        const associationTypeName = event.payload?.association_type_name as
          | string
          | undefined;
        const targetEntityId = event.payload?.target_entity_id as
          | string
          | undefined;
        if (!associationTypeName || !targetEntityId) {
          break;
        }
        if (createdIds.has(event.entity_id)) {
          // Newly-created in this batch — track the pending association so list
          // hooks filtering by associated_with_entity_id can inject the object.
          addPendingAssociation(event.entity_id, targetEntityId);
        } else {
          // Existing entity — apply the association change to the store now so
          // subscribers (e.g. the slideout via subscribeToStoredObject) update
          // immediately, before the WS broadcast arrives.
          applyAssociationChange(
            event.entity_id,
            'added',
            associationTypeName,
            targetEntityId,
            event.seq
          );
        }
        break;
      }

      case 'ahoy_association_removed': {
        const associationTypeName = event.payload?.association_type_name as
          | string
          | undefined;
        const targetEntityId = event.payload?.target_entity_id as
          | string
          | undefined;
        if (!associationTypeName || !targetEntityId) {
          break;
        }
        applyAssociationChange(
          event.entity_id,
          'removed',
          associationTypeName,
          targetEntityId,
          event.seq
        );
        // Also clear any pending-creation association tracking so filtered
        // list hooks (e.g. associated_with_entity_id) stop injecting this
        // entity under the removed association. No-op for entities that
        // aren't tracked as pending creations.
        removePendingAssociation(event.entity_id, targetEntityId);
        break;
      }
    }
  }
}

/**
 * Client for submitting events to the event-sourced CRM.
 */
export class EventsClient {
  constructor(private orgId: number) {}

  /**
   * Submit a batch of events to the CRM.
   *
   * @param events - Array of events to submit
   * @param overwriteTargets - Optional per-conflict authorizations to overwrite
   *   existing single-cardinality associations, forwarded to the backend as the
   *   batch-level `overwrite_targets`. Required when the batch removes-then-adds
   *   a single-cardinality association the user has acknowledged (e.g. a nested
   *   draft whose association overwrites an existing record); omitting it makes
   *   the backend reject the whole atomic batch with a cardinality violation.
   *   Omitted from the wire payload entirely when empty/undefined.
   * @returns Response with processed count and any created entity mappings
   * @throws PropertyTypeValidationError for 400 validation errors
   * @throws PropertyTypePermissionError for 403 permission errors
   * @throws PropertyTypeError for other API errors
   */
  async submitEvents(
    events: BaseEvent[],
    overwriteTargets?: OverwriteTarget[]
  ): Promise<EventBatchResponse> {
    const url = `/api/orgs/${this.orgId}/crm-events/events/`;
    const batch: EventBatch = {
      events,
      ...(overwriteTargets?.length
        ? { overwrite_targets: overwriteTargets }
        : {}),
    };

    try {
      const response = await apiCall<EventBatchResponse>(url, 'POST', batch);
      // Prefer response results for optimistic updates — the backend may rewrite
      // events (e.g., unique constraint validator rewrites object_created to
      // property_updated on a hidden entity). Fall back to submitted events if
      // the response doesn't include enriched results.
      processOptimisticUpdates(response.results ?? events, this.orgId);
      return response;
    } catch (error) {
      throw this.wrapError(error);
    }
  }

  /**
   * Wrap API errors in typed PropertyTypeError hierarchy.
   */
  private wrapError(error: unknown): PropertyTypeError {
    if (error instanceof PropertyTypeError) {
      return error;
    }

    const apiError = error as {
      message?: string;
      statusCode?: number;
      response?: {
        correlation_id?: string;
        error?: string;
        error_type?: string;
        detail?: string;
      };
    };

    const rawMessage =
      apiError.response?.error ||
      apiError.response?.detail ||
      apiError.message ||
      'Unknown error';
    const errorType = apiError.response?.error_type;
    const correlationId = apiError.response?.correlation_id;
    const { statusCode } = apiError;

    logger.debug('CRM event error', {
      error_type: errorType,
      raw_message: rawMessage,
      status_code: statusCode,
    });

    const message = translateEventError(errorType, rawMessage);

    if (statusCode === 409) {
      const detail = apiError.response?.detail as
        | {
            expected_seq?: number;
            last_seq?: number;
            entity_id?: string;
            last_changed_by?: {
              name: string;
              source_type: string;
              source_id?: string;
            };
          }
        | undefined;
      const err = new PropertyTypeConflictError(message, detail, correlationId);
      if (errorType) {
        err.code = errorType;
      }
      return err;
    }

    if (statusCode === 400) {
      const err = new PropertyTypeValidationError(message, correlationId);
      if (errorType) {
        err.code = errorType;
      }
      return err;
    }

    if (statusCode === 403) {
      const err = new PropertyTypePermissionError(message, correlationId);
      if (errorType) {
        err.code = errorType;
      }
      return err;
    }

    return new PropertyTypeError(
      message,
      errorType || 'EVENTS_ERROR',
      correlationId
    );
  }
}

/**
 * Factory function for creating events clients.
 */
export function createEventsClient(orgId: number): EventsClient {
  return new EventsClient(orgId);
}

// Re-export for use by CRMObjectClient (deprecated path)
export { processOptimisticUpdates };
